import * as React from "react";
import {
    approvePayrollRun,
    calculatePayrollRun,
    Contract,
    finalizePayrollPeriod,
    getContractsByPeriodKey,
    PayrollWorkflowConflictError,
} from "../services/payrollService";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {usePeriod, ALL_BRANCHES_VALUE} from "../context/PeriodContext";
import {API_BASE_URL} from "../config/apiConfig";
import type {BankAccount} from "../types/contract";
import {PAYROLL_RUN_COLUMNS, type PayrollRunColumnKey} from "../components/Payroll/payrollperiods/payrollRunColumns";

export interface BreakdownRow {
    label: string;
    amount: number;
    meta: Record<string, unknown>;
}

export interface PayrollDetails {
    grossChangesPositive: number;
    grossChangesNegative: number;
    pensionEmployeePreTarget: number;
    taxPreTarget: number;
    netPreTarget: number;
    netChangesPositive: number;
    netChangesNegative: number;
}

export interface PayrollCalculation {
    employeeNumber: string;
    name: string;
    branch: string;
    gross: number;
    tax: string;
    pension: number;
    netPay: number;
    bankNet: number;
    details?: PayrollDetails | null;
    // Single active bank account resolved by the backend; null when the employee has none yet.
    bankAccount?: BankAccount | null;
    // Additional fields surfaced by the backend for the redesigned Payroll Run summary/table
    baseGross?: number;
    grossAfterGrossChanges?: number;
    pensionEmployee?: number;
    pensionEmployer?: number;
    familyInsuranceDeduction?: number;
    fundDeduction?: number;
    employerCostTotal?: number;
    // Identifiers used to cross-reference the separately-fetched contracts list (see `contracts`)
    contractId?: string;
    employeeId?: string;
}

export interface ReconciliationData {
    earnings: {
        baseGross: number;
        grossAdditions: number;
        grossDeductions: number;
        netToGrossAdjustment: number;
        finalGross: number;
    };
    employeeDeductions: {
        pensionEmployee: number;
        incomeTax: number;
        netBeforeOtherDeductions: number;
    };
    otherAdjustments: {
        netAdditions: number;
        netDeductions: number;
        familyInsurance: number;
        voluntaryFund: number;
        netToBank: number;
    };
    employerCost: {
        finalGross: number;
        employerPension: number;
        employerPaidBenefits: number;
        totalEmployerCost: number;
    };
}

// Per-employee equivalent of ReconciliationData, shaped to match the "Summary" tab of the
// per-employee Payroll Run Details dialog (see PayrollRunDetailsPerEmployee.tsx). Unlike the
// aggregate reconciliation, card 4 here is "Net Pay" rather than "Employer Cost" — employer cost
// is shown in its own side panel instead.
export interface EmployeeSummaryData {
    earnings: {
        baseGross: number;
        grossAdditions: number;
        grossDeductions: number;
        netToGrossAdjustment: number;
        finalGross: number;
    };
    statutoryDeductions: {
        pensionEmployee: number;
        pensionEmployeePct: number;
        incomeTax: number;
        totalStatutoryDeductions: number;
    };
    otherAdjustments: {
        netAdditions: number;
        netDeductions: number;
        familyInsurance: number;
        voluntaryFund: number;
        totalOtherDeductions: number;
    };
    netPay: {
        netBeforeOtherDeductions: number;
        totalOtherDeductions: number;
        netToBank: number;
    };
    employerCost: {
        finalGross: number;
        employerPension: number;
        employerPensionPct: number;
        employerPaidBenefits: number;
        totalEmployerCost: number;
    };
}

/**
 * Builds the per-employee "Summary" tab figures for the Payroll Run Details dialog from a single
 * PayrollCalculation row. Mirrors the aggregate reconciliation math in usePayrollRun, applied to
 * one employee instead of totals across all employees.
 */
export function buildEmployeeSummary(employee: PayrollCalculation): EmployeeSummaryData {
    const baseGross = employee.baseGross ?? 0;
    const finalGross = employee.gross ?? 0;
    const grossAdditions = employee.details?.grossChangesPositive ?? 0;
    const grossDeductions = employee.details?.grossChangesNegative ?? 0;
    const netToGrossAdjustment = finalGross - (baseGross + grossAdditions - grossDeductions);

    const pensionEmployee = employee.pensionEmployee ?? 0;
    const pensionEmployeePct = baseGross > 0 ? Math.round((pensionEmployee / baseGross) * 100) : 0;
    const incomeTax = Number(employee.tax) || 0;
    const totalStatutoryDeductions = pensionEmployee + incomeTax;

    const netAdditions = employee.details?.netChangesPositive ?? 0;
    const netDeductions = employee.details?.netChangesNegative ?? 0;
    const familyInsurance = employee.familyInsuranceDeduction ?? 0;
    const voluntaryFund = employee.fundDeduction ?? 0;
    const totalOtherDeductions = netDeductions + familyInsurance + voluntaryFund - netAdditions;

    const netBeforeOtherDeductions = finalGross - totalStatutoryDeductions;
    const netToBank = employee.bankNet ?? 0;

    const employerPension = employee.pensionEmployer ?? 0;
    const employerPensionPct = baseGross > 0 ? Math.round((employerPension / baseGross) * 100) : 0;
    const totalEmployerCost = employee.employerCostTotal ?? 0;
    const employerPaidBenefits = totalEmployerCost - finalGross - employerPension;

    return {
        earnings: {baseGross, grossAdditions, grossDeductions, netToGrossAdjustment, finalGross},
        statutoryDeductions: {pensionEmployee, pensionEmployeePct, incomeTax, totalStatutoryDeductions},
        otherAdjustments: {netAdditions, netDeductions, familyInsurance, voluntaryFund, totalOtherDeductions},
        netPay: {netBeforeOtherDeductions, totalOtherDeductions, netToBank},
        employerCost: {finalGross, employerPension, employerPensionPct, employerPaidBenefits, totalEmployerCost},
    };
}

interface PayrollCalculationResponse {
    totalRecords: number;
    payrollCalculations: PayrollCalculation[];
    totalGross: number;
    totalTax: number;
    totalPensionEmp: number;
    totalPensionEmpr: number;
    totalNetPay: number;
    breakdownRows: BreakdownRow[];
}

// ─── Sorting helpers ─────────────────────────────────────────────────────────
type Order = "asc" | "desc";
type SortableKey = "name" | "branch" | "gross" | "tax" | "pension" | "netPay" | "bankNet";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function usePayrollRun() {
    const {token} = useAuth();
    const {t} = useTranslation();
    // Period + branch are both read from the global PeriodContext — the top toolbar
    // (desktop)/mobile filter bar under the tabs is the single source of truth; this hook must
    // not keep its own parallel selection (see docs/PAYROLL_RUN_LAYOUT.md).
    const {selectedPeriod, selectedPeriodValue, dashboardBranch, allPeriods, refreshPeriods} = usePeriod();
    const selectedBranch = dashboardBranch;

    const isPeriodOpen = selectedPeriodValue?.includes("OPEN") ?? false;
    const isPeriodLocked = selectedPeriodValue?.toUpperCase().includes("LOCKED") ?? false;

    // Re-fetch periods (incl. workflowStage) whenever Payroll Run mounts, so a workflow
    // transition made elsewhere (e.g. a Period-page lock, or finalizing from another tab) is
    // reflected here without requiring a full page reload.
    React.useEffect(() => {
        refreshPeriods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The legacy `status` column only knows OPEN/LOCKED and can't tell us *why* a period is
    // locked: an admin may have locked it directly from the Period page (to freeze employee
    // data on purpose) without having finalized the payroll run yet. `workflowStage` disambiguates
    // this — it only reaches FINALIZED once "Check and finalize" has actually succeeded for every
    // branch. See PayrollWorkflowStage.java.
    const periodWorkflowStage = React.useMemo(
        () => allPeriods.find((p) => p.periodKey === selectedPeriod)?.workflowStage,
        [allPeriods, selectedPeriod],
    );
    const isPeriodFinalized = periodWorkflowStage === "FINALIZED";
    // "Check and Finalize" is a period-scoped action (it locks/finalizes the whole period, not
    // just one branch's run) — calculation can be done per-branch, but finalizing requires the
    // "All" branches filter so the admin isn't misled into thinking a single-branch finalize is
    // branch-scoped.
    const isAllBranchesSelected = selectedBranch === ALL_BRANCHES_VALUE;
    // A period locked from the Period page (not yet finalized) must not block "Check and
    // finalize" — it only means employee data can no longer be edited. Only disable the button
    // once the run has actually been finalized, or when a single branch (rather than "All") is
    // selected.
    const isFinalizeDisabled = isPeriodFinalized || !isAllBranchesSelected;
    // Standing warning shown on the page whenever the period is locked but not yet finalized.
    const isPeriodLockedNotYetFinalized = isPeriodLocked && !isPeriodFinalized;
    const [contracts, setContracts] = React.useState<Contract[]>([]);
    const [contractsLoading, setContractsLoading] =
        React.useState<boolean>(false);
    const [contractsPage, setContractsPage] = React.useState<number>(0);
    const [searchInput, setSearchInput] = React.useState<string>("");
    const [searchQuery, setSearchQuery] = React.useState<string>("");
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [periodError, setPeriodError] = React.useState<string>("");
    const [payrollCalculations, setPayrollCalculations] = React.useState<
        PayrollCalculation[]
    >([]);
    const [totalGross, setTotalGross] = React.useState<number>(0);
    const [totalTax, setTotalTax] = React.useState<number>(0);
    const [totalPensionEmp, setTotalPensionEmp] = React.useState<number>(0);
    const [totalPensionEmpr, setTotalPensionEmpr] = React.useState<number>(0);
    const [totalNetPay, setTotalNetPay] = React.useState<number>(0);
    const [breakdownRows, setBreakdownRows] = React.useState<BreakdownRow[]>([]);
    const [detailsDialogOpen, setDetailsDialogOpen] =
        React.useState<boolean>(false);
    const [selectedEmployee, setSelectedEmployee] =
        React.useState<PayrollCalculation | null>(null);
    const itemsPerPage = 10;

    // ─── Column visibility (desktop table + mobile card fields share this) ──────────────────────
    const [visibleColumnIds, setVisibleColumnIds] = React.useState<Set<PayrollRunColumnKey> | null>(null);
    const isColumnVisible = React.useCallback(
        (columnId: PayrollRunColumnKey) => visibleColumnIds === null || visibleColumnIds.has(columnId),
        [visibleColumnIds],
    );
    const handleToggleColumnVisibility = (columnId: PayrollRunColumnKey) => {
        setVisibleColumnIds((prev) => {
            const current = prev ?? new Set(PAYROLL_RUN_COLUMNS.map((c) => c.id));
            const next = new Set(current);
            if (next.has(columnId)) next.delete(columnId); else next.add(columnId);
            return next;
        });
    };
    const handleShowAllColumns = () => setVisibleColumnIds(null);
    const visibleColumnCount = visibleColumnIds?.size ?? PAYROLL_RUN_COLUMNS.length;

    // ─── Sorting state ──────────────────────────────────────────────────────────
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<SortableKey>("name");

    const handleRequestSort = (property: SortableKey) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedAndFilteredCalculations = React.useMemo(() => {
        const filtered = searchQuery
            ? payrollCalculations.filter(
                (emp) =>
                    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    emp.employeeNumber.toLowerCase().includes(searchQuery.toLowerCase()),
            )
            : payrollCalculations;
        return [...filtered].sort(getComparator(order, orderBy));
    }, [payrollCalculations, searchQuery, order, orderBy]);

    // ─── Summary card + reconciliation derivations ──────────────────────────────
    // Headcount and a couple of totals aren't aggregated server-side, so they're derived
    // client-side from the already-fetched payrollCalculations list.
    const employeeCount = payrollCalculations.length;

    const totalBankNet = React.useMemo(
        () => payrollCalculations.reduce((sum, emp) => sum + (emp.bankNet ?? 0), 0),
        [payrollCalculations],
    );

    const totalEmployerCost = React.useMemo(
        () => payrollCalculations.reduce((sum, emp) => sum + (emp.employerCostTotal ?? 0), 0),
        [payrollCalculations],
    );

    const totalFamilyInsurance = React.useMemo(
        () => payrollCalculations.reduce((sum, emp) => sum + (emp.familyInsuranceDeduction ?? 0), 0),
        [payrollCalculations],
    );

    const totalFundDeduction = React.useMemo(
        () => payrollCalculations.reduce((sum, emp) => sum + (emp.fundDeduction ?? 0), 0),
        [payrollCalculations],
    );

    const totalBaseGross = React.useMemo(
        () => payrollCalculations.reduce((sum, emp) => sum + (emp.baseGross ?? 0), 0),
        [payrollCalculations],
    );

    // Looks up an aggregated breakdown amount by its backend label (see PayrollBreakdownLabels).
    const getBreakdownAmount = React.useCallback(
        (label: string): number => breakdownRows.find((row) => row.label === label)?.amount ?? 0,
        [breakdownRows],
    );

    // Builds the 4-column Earnings / Employee Deductions / Other Adjustments / Employer Cost
    // reconciliation. Trusted totals (totalGross, totalTax, totalPensionEmp/Empr, totalBankNet,
    // totalEmployerCost) anchor each column; the two "adjustment" plug figures absorb whatever
    // gap remains between the itemized breakdown rows and those trusted totals so each column
    // still ties out exactly.
    const reconciliation: ReconciliationData | null = React.useMemo(() => {
        if (payrollCalculations.length === 0) return null;

        const grossAdditions = getBreakdownAmount("Gross changes (+)");
        const grossDeductions = getBreakdownAmount("Gross changes (-)");
        const netToGrossAdjustment = totalGross - (totalBaseGross + grossAdditions - grossDeductions);

        const netBeforeOtherDeductions = totalGross - totalPensionEmp - totalTax;

        const netAdditions = getBreakdownAmount("Net changes (+)");
        const netDeductions = getBreakdownAmount("Net changes (-)");

        const employerPaidBenefits = totalEmployerCost - totalGross - totalPensionEmpr;

        return {
            earnings: {
                baseGross: totalBaseGross,
                grossAdditions,
                grossDeductions,
                netToGrossAdjustment,
                finalGross: totalGross,
            },
            employeeDeductions: {
                pensionEmployee: totalPensionEmp,
                incomeTax: totalTax,
                netBeforeOtherDeductions,
            },
            otherAdjustments: {
                netAdditions,
                netDeductions,
                familyInsurance: totalFamilyInsurance,
                voluntaryFund: totalFundDeduction,
                netToBank: totalBankNet,
            },
            employerCost: {
                finalGross: totalGross,
                employerPension: totalPensionEmpr,
                employerPaidBenefits,
                totalEmployerCost,
            },
        };
    }, [
        payrollCalculations.length,
        getBreakdownAmount,
        totalBaseGross,
        totalGross,
        totalPensionEmp,
        totalTax,
        totalFamilyInsurance,
        totalFundDeduction,
        totalBankNet,
        totalPensionEmpr,
        totalEmployerCost,
    ]);

    // Helper to safely format numbers as EUR. Returns '-' when value is null/undefined/invalid
    const formatEUR = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        if (typeof value !== "number" || !Number.isFinite(value)) return "-";
        try {
            return value.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
            });
        } catch (e) {
            return `€${value.toFixed(2)}`;
        }
    };

    // Fetch contracts whenever the (globally-selected) period changes, including the initial
    // mount value restored from session — period selection lives in PeriodContext, not here.
    React.useEffect(() => {
        if (token && selectedPeriod) {
            setContractsLoading(true);
            getContractsByPeriodKey(token, selectedPeriod)
                .then((response) => {
                    setContracts(response.contracts);
                    setContractsPage(0);
                })
                .catch((error) => {
                    console.error("Failed to fetch contracts:", error);
                    setContracts([]);
                })
                .finally(() => setContractsLoading(false));
        } else {
            setContracts([]);
        }
    }, [token, selectedPeriod]);

    const [calculating, setCalculating] = React.useState<boolean>(false);

    const handleCalculatePayroll = async () => {
        // Validate that a period is selected
        if (!selectedPeriod || selectedPeriod === null || selectedPeriod === "") {
            setPeriodError(t('payroll.run.error.selectPeriodToCalculate'));
            return;
        }

        // Clear any previous error
        setPeriodError("");

        console.log("Calculate payroll clicked");
        console.log("Selected Period:", selectedPeriod);
        console.log("Selected Branch:", selectedBranch);

        if (!token) {
            console.error("No authentication token available");
            return;
        }

        try {
            setCalculating(true);
            const response = await fetch(
                `${API_BASE_URL}/api/payroll/run?branch=${selectedBranch}&periodKey=${selectedPeriod}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message ||
                    `Failed to calculate payroll: ${response.statusText}`,
                );
            }

            const data: PayrollCalculationResponse = await response.json();
            console.log("Payroll calculation response:", data);

            // Populate the table with the response data
            setPayrollCalculations(data.payrollCalculations);
            setTotalGross(data.totalGross);
            setTotalTax(data.totalTax);
            setTotalPensionEmp(data.totalPensionEmp);
            setTotalPensionEmpr(data.totalPensionEmpr);
            setTotalNetPay(data.totalNetPay);
            setBreakdownRows(data.breakdownRows ?? []);
            setCurrentPage(1); // Reset to first page when new data is loaded
        } catch (error) {
            console.error("Error calculating payroll:", error);
            // Clear table on error
            setPayrollCalculations([]);
            setTotalGross(0);
            setTotalTax(0);
            setTotalPensionEmp(0);
            setTotalPensionEmpr(0);
            setTotalNetPay(0);
            setBreakdownRows([]);
        } finally {
            setCalculating(false);
        }
    };

    const [downloading, setDownloading] = React.useState<boolean>(false);

    const downloadPayroll = async () => {
        if (!selectedPeriod) {
            setPeriodError(t('payroll.run.error.selectPeriodToDownload'));
            return;
        }
        setPeriodError("");
        if (!token) {
            console.error("No authentication token available");
            return;
        }
        setDownloading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/payroll/run/download?branch=${selectedBranch}&periodKey=${selectedPeriod}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Download failed: ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const contentDisposition = response.headers.get("Content-Disposition");
            const filename = contentDisposition
                ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") ?? "payroll.xlsx"
                : "payroll.xlsx";
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading payroll:", error);
        } finally {
            setDownloading(false);
        }
    };

    // ─── Workflow: Calculate → Approve → Finalize ("Kontrollo dhe finalizo") ────────────────────
    // Persists the current (period, branch) calculation as an authoritative BatchPayrollRun,
    // approves it, then tries to finalize the period. Finalization only actually succeeds once
    // every branch covered by this period has an approved run — a 409 from the finalize step in
    // that case is an expected, informational outcome, not a hard error.
    const [finalizeDialogOpen, setFinalizeDialogOpen] = React.useState<boolean>(false);
    const [finalizing, setFinalizing] = React.useState<boolean>(false);
    const [finalizeError, setFinalizeError] = React.useState<string>("");
    const [finalizeInfo, setFinalizeInfo] = React.useState<string>("");
    const [periodFinalized, setPeriodFinalized] = React.useState<boolean>(false);
    // Page-level success notification (Snackbar) — separate from `finalizeInfo` above, which is
    // only rendered *inside* the confirm dialog and therefore was never actually visible on the
    // success path (the dialog closes in the same step that sets it).
    const [finalizeSuccessMessage, setFinalizeSuccessMessage] = React.useState<string>("");

    const handleOpenFinalizeDialog = () => {
        setFinalizeError("");
        setFinalizeInfo("");
        setFinalizeDialogOpen(true);
    };

    const handleCloseFinalizeDialog = () => {
        if (finalizing) return;
        setFinalizeDialogOpen(false);
    };

    const handleConfirmFinalize = async () => {
        if (!selectedPeriod || !token) {
            setFinalizeError(t('payroll.run.error.selectPeriodToCalculate'));
            return;
        }
        if (!isAllBranchesSelected) {
            // Defensive guard: the button/menu item should already be disabled in this case,
            // but a stale branch-filter change in-flight shouldn't be able to trigger a
            // single-branch "finalize" of the whole period.
            setFinalizeError(t('payroll.run.finalize.allBranchesRequired'));
            return;
        }
        setFinalizing(true);
        setFinalizeError("");
        setFinalizeInfo("");
        try {
            const calculated = await calculatePayrollRun(token, selectedPeriod, selectedBranch);
            const approved = await approvePayrollRun(token, calculated.runId);
            try {
                await finalizePayrollPeriod(token, selectedPeriod);
                setPeriodFinalized(true);
                setFinalizeDialogOpen(false);
                setFinalizeSuccessMessage(t('payroll.run.finalize.success'));
                // Finalizing also locks the period server-side — refresh so isPeriodLocked /
                // workflowStage (and therefore the button/warning) reflect that immediately.
                void refreshPeriods();
            } catch (finalizeErr) {
                if (finalizeErr instanceof PayrollWorkflowConflictError) {
                    // Expected when other branches aren't approved yet — this branch is still
                    // successfully approved.
                    setFinalizeInfo(
                        approved.periodFullyApproved
                            ? t('payroll.run.finalize.success')
                            : t('payroll.run.finalize.branchApprovedPending'),
                    );
                } else {
                    throw finalizeErr;
                }
            }
        } catch (error) {
            setFinalizeError(error instanceof Error ? error.message : t('payroll.run.finalize.error'));
        } finally {
            setFinalizing(false);
        }
    };

    return {
        selectedPeriod,
        selectedPeriodValue,
        isPeriodOpen,
        isPeriodLocked,
        isPeriodFinalized,
        isFinalizeDisabled,
        isAllBranchesSelected,
        isPeriodLockedNotYetFinalized,
        contracts,
        contractsLoading,
        contractsPage,
        selectedBranch,
        searchInput,
        setSearchInput,
        searchQuery,
        setSearchQuery,
        currentPage,
        setCurrentPage,
        periodError,
        payrollCalculations,
        totalGross,
        totalTax,
        totalPensionEmp,
        totalPensionEmpr,
        totalNetPay,
        breakdownRows,
        employeeCount,
        totalBankNet,
        totalEmployerCost,
        reconciliation,
        detailsDialogOpen,
        setDetailsDialogOpen,
        selectedEmployee,
        setSelectedEmployee,
        itemsPerPage,
        order,
        orderBy,
        handleRequestSort,
        sortedAndFilteredCalculations,
        formatEUR,
        isColumnVisible,
        handleToggleColumnVisibility,
        handleShowAllColumns,
        visibleColumnCount,
        handleCalculatePayroll,
        calculating,
        downloadPayroll,
        downloading,
        finalizeDialogOpen,
        finalizing,
        finalizeError,
        finalizeInfo,
        finalizeSuccessMessage,
        setFinalizeSuccessMessage,
        periodFinalized,
        handleOpenFinalizeDialog,
        handleCloseFinalizeDialog,
        handleConfirmFinalize,
    };
}
